import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useCreate, useGo } from '@refinedev/core';
import { Create, useAutocomplete } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { isEqual } from 'lodash';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteInput } from '@attackiq/components';
import { ATTACKIQ_ID, MODULE_CONTENT_MAP } from '@attackiq/constants';

import useSearchParams from '../../hooks/useSearchParams';
import { BlueprintTemplate, ProjectTemplate, Scenario, AttackGraphTemplate, FlexPackage, Module } from '../../types';

const contentTypeOptions = Object.entries(MODULE_CONTENT_MAP).map(([key, value]) => ({
  model: key,
  displayName: value
}));

const ModuleContentCreate = () => {
  const { saveButtonProps, ...formMethods } = useForm({
    refineCoreProps: {
      resource: 'module_contents',
      action: 'create',
      redirect: false
    }
  });
  const go = useGo();
  const { mutateAsync: createAsync } = useCreate();

  const { handleSubmit } = formMethods;

  const onSubmit = handleSubmit(async ({ content, content_type, module }) => {
    const promises = content.map(async content => {
      await createAsync({
        resource: 'module_contents',
        values: {
          module_id: module.id,
          content_id: content.id,
          content_type_model: content_type.model,
          company_id: ATTACKIQ_ID,
          content_type_app_label: 'ai_cloud'
        }
      });
    });

    await Promise.all(promises);
    go({
      to: {
        resource: 'modules',
        action: 'show',
        id: module.id
      }
    });
  });

  return (
    <FormProvider {...formMethods}>
      <form name="create-module-content-form" onSubmit={onSubmit}>
        <Create saveButtonProps={{ type: 'submit' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ModuleInput />
            <ContentTypeInput />
            <ContentInput />
          </Box>
        </Create>
      </form>
    </FormProvider>
  );
};

const ModuleInput = () => {
  const { setValue } = useFormContext();
  const { autocompleteProps } = useAutocomplete<Module>({
    resource: 'modules',
    pagination: {
      pageSize: 10000
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });
  const { options } = autocompleteProps;

  const { module_id } = useSearchParams();

  useEffect(() => {
    if (module_id && options) {
      const currentModule = options.find(option => option.id === module_id) || null;

      setValue('module', currentModule);
    }
  }, [setValue, module_id, options]);

  return (
    <AutocompleteInput
      {...autocompleteProps}
      options={[...autocompleteProps.options]}
      sx={{ mt: 0.5 }}
      name="module"
      defaultValue={null}
      getOptionLabel={option => option.display_name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{
        label: 'Module'
      }}
    />
  );
};

const ContentTypeInput = () => {
  const contentType = useWatch({ name: 'content_type', defaultValue: null });
  const { setValue } = useFormContext();

  return (
    <AutocompleteInput
      name="content_type"
      defaultValue={null}
      options={contentTypeOptions}
      onChange={(_event, value) => {
        if (!isEqual(value, contentType)) {
          setValue('content', []);
        }
      }}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;

        return option.displayName || '';
      }}
      isOptionEqualToValue={(option, value) => option.model === value.model}
      TextFieldProps={{
        label: 'Content Type'
      }}
    />
  );
};

const ContentInput = () => {
  const contentType: (typeof contentTypeOptions)[number] = useWatch({ name: 'content_type', defaultValue: null });
  const isScenario = contentType?.model === 'scenario';
  const isAssessmentTemplate = contentType?.model === 'projecttemplate';
  const isBlueprint = contentType?.model === 'blueprinttemplate';
  const isAttackGraphTemplate = contentType?.model === 'attackgraphtemplate';
  const isFlexPackage = contentType?.model === 'flexpackage';
  const { autocompleteProps: scenariosAutocompleteProps } = useAutocomplete<Scenario>({
    resource: 'scenarios',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: templatesAutocompleteProps } = useAutocomplete<ProjectTemplate>({
    resource: 'assessment_templates',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: blueprintsAutocompleteProps } = useAutocomplete<BlueprintTemplate>({
    resource: 'blueprint_templates',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: attackgraphTemplatesAutocompleteProps } = useAutocomplete<AttackGraphTemplate>({
    resource: 'attack_graph_templates',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: FlexPackageAutocompleteProps } = useAutocomplete<FlexPackage>({
    resource: 'flex_packages',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const getOptions = () => {
    const mapOptions = (options: readonly { id: string; name?: string; template_name?: string }[]) => {
      return options.map(option => ({ id: option.id, label: option.name || option.template_name || '' }));
    };

    if (isScenario) return mapOptions(scenariosAutocompleteProps.options);

    if (isAssessmentTemplate) return mapOptions(templatesAutocompleteProps.options);

    if (isBlueprint) return mapOptions(blueprintsAutocompleteProps.options);

    if (isAttackGraphTemplate) return mapOptions(attackgraphTemplatesAutocompleteProps.options);

    if (isFlexPackage) return mapOptions(FlexPackageAutocompleteProps.options);

    return [];
  };

  const options = getOptions();

  return (
    <AutocompleteInput
      options={options}
      name="content"
      defaultValue={[]}
      disabled={!contentType}
      getOptionLabel={option => option.label || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{ label: 'Content' }}
      multiple
    />
  );
};

export default ModuleContentCreate;
